import { UrlTargetType } from './constants/urlConstants';
import { actions, IStore } from './store';
import { Dispatch } from '@reduxjs/toolkit';
import { generateApiStoreKey } from './utils';
import { ApiNameType, ISubscribe, ResourceKey, StoreNameType } from './models/subscriptions';
import { IWebApiRequestBody, webTradingApi, createApiClient, ApiResponse } from '@cmctechnology/webinvest-api-client-web-trading';
import { frontendActions } from '.';
import { getConfig } from './configuration/configuration';
import min from 'lodash.min';
import flatten from 'lodash.flatten';
import { getFRTokens } from './utils/FRAuthHelper';

export const redirectToUrl = (url: string, target: UrlTargetType, features: string | undefined = undefined) => {
  return window.open(url, target, features);
};

type APICall<T> = (...args: any) => Promise<T>;

export const apiCall =
  <ResultType>(storeName: StoreNameType, apiName: ApiNameType, apiCallFn: APICall<ResultType>, key?: ResourceKey, dispatchManually?: boolean) =>
  async (dispatch: Dispatch<any>) => {
    const storeKey = generateApiStoreKey(storeName, apiName, key);
    dispatch(actions.apiRequestSent(storeKey));
    try {
      const response = await apiCallFn();
      if (!dispatchManually) dispatch(actions.apiRequestSucceeded(storeKey));
      return response;
    } catch (error: any) {
      if (!dispatchManually) dispatch(actions.apiRequestFailed({ apiRequest: storeKey, errorCode: error?.errorCode }));
      throw error;
    }
  };

export const webTradingApiCall = async <TModel>(
  storeName: string,
  apiName: string,
  request: IWebApiRequestBody,
  dispatch: Dispatch<any>,
  key: ResourceKey | undefined = undefined,
  overridePending: boolean | undefined = undefined,
  includeResponseInfo: boolean | undefined = undefined,
  dispatchManually: boolean | undefined = undefined,
  immediateDispatch: boolean | undefined = undefined,
  enablePingSession: boolean | undefined = undefined
) => {
  const apiCallFn = () =>
    webTradingApi(getConfig().stockbrokingApiUrl).send<TModel>(
      { [apiName]: request },
      overridePending,
      includeResponseInfo,
      immediateDispatch,
      enablePingSession
    );
  const response = await frontendActions.apiCall(storeName, apiName, apiCallFn, key, dispatchManually)(dispatch);
  return response;
};

export const webTradingApiCallDirect = async <TModel>(
  apiName: string,
  request: IWebApiRequestBody,
  overridePending: boolean | undefined = undefined,
  includeResponseInfo: boolean | undefined = undefined,
  immediateDispatch: boolean | undefined = undefined,
  enablePingSession: boolean | undefined = undefined
) => {
  const response = await webTradingApi(getConfig().stockbrokingApiUrl).send<TModel>(
    { [apiName]: request },
    overridePending,
    includeResponseInfo,
    immediateDispatch,
    enablePingSession
  );
  return response;
};

type PollType<T> = Pick<ISubscribe<T>, 'onPoll'> & { storeKey: ReturnType<typeof generateApiStoreKey> };

export const poll =
  <ResultType>(args: PollType<ResultType>) =>
  (dispatch: Dispatch<any>, getState: () => IStore) => {
    const { storeKey, onPoll } = args;

    const storeSubscriptionTimeouts = Object.values(getState().frontend.subscriptions?.[storeKey] ?? []);
    const storeSubscriptions = Object.keys(getState().frontend.subscriptions?.[storeKey] ?? []);
    const polling = storeSubscriptionTimeouts.length > 0;

    // NOTE: this is temporary, finding the shortest timeout in store subscriptions
    const timeouts = flatten(storeSubscriptionTimeouts);
    const pollTimeout = min(timeouts);

    if (onPoll) {
      dispatch(onPoll(storeSubscriptions));
    }

    if (polling && onPoll) {
      setTimeout(() => {
        dispatch(poll(args));
      }, pollTimeout);
    }
  };

export const subscribe =
  <ResultType>(args: ISubscribe<ResultType>) =>
  (dispatch: Dispatch<any>, getState: () => IStore) => {
    const { location, resourceKeys = [''], storeName, apiName, pollTimeout, onPoll } = args;
    const storeKey = generateApiStoreKey(storeName, apiName);

    const subscriptions = Object.keys(getState().frontend.subscriptions?.[storeKey] ?? []);
    const polling = subscriptions.length > 0;
    const notPollingResourceKeys = resourceKeys.filter((resourceKey) => !subscriptions.includes(resourceKey.toString()));

    dispatch(actions.subscribed({ location, storeKey, pollTimeout, resourceKeys }));

    if (!polling && onPoll) {
      dispatch(poll({ ...args, storeKey }));
    }

    // Only prefetch if polling already started
    if (polling && onPoll && notPollingResourceKeys.length > 0) {
      dispatch(onPoll(notPollingResourceKeys));
    }
  };

export const unsubscribe =
  <ResultType>(args: Pick<ISubscribe<ResultType>, 'location' | 'resourceKeys' | 'storeName' | 'apiName'>) =>
  (dispatch: Dispatch<any>) => {
    const { location, resourceKeys = [''], storeName, apiName } = args;
    const storeKey = generateApiStoreKey(storeName, apiName);

    dispatch(actions.unsubscribed({ location, storeKey, resourceKeys }));
  };

export const { apiRequestFailed, apiRequestReset, apiRequestSent, apiRequestSucceeded } = actions;

export const cloudApiCall = async <TRequest, TResponse>(
  storeName: string,
  endpoint: string,
  data: TRequest,
  dispatch: Dispatch<any>,
  key: ResourceKey | undefined = undefined,
  isGet?: boolean
) => {
  const apiCallFn = async () => {
    const tokens = (await getFRTokens()) as any;
    if (!tokens?.accessToken) {
      throw new Error('Invalid ForgeRock token from getFRTokens()');
    }

    const client = createApiClient({ baseUrl: getConfig().cloudApiUrl, forgeRockAccessToken: tokens.accessToken });
    let response: ApiResponse<TResponse>;
    response = await (isGet ? client.get<TResponse>(endpoint) : client.post<TRequest, TResponse>(endpoint, data));

    return response;
  };

  const response = await frontendActions.apiCall(storeName, endpoint, apiCallFn, key, false)(dispatch);
  return response;
};
